import React, { useState, useEffect, useCallback } from "react";
import {
  Modal,
  View,
  StyleSheet,
  TouchableOpacity,
  TouchableWithoutFeedback,
  ActivityIndicator,
} from "react-native";
import { GestureHandlerRootView } from "react-native-gesture-handler";
import { RefundProcessItem, isBill } from "@/utils/refundProcessTypeGuards";
import { BillWithRefundProcess } from "@/types/bill";
import { ProductPrice } from "@/types/productPrice";
import { AntDesign } from "@expo/vector-icons";
import ConfirmCallContent from "./ConfirmCallContent";
import { InitiateCallContent } from "./InitiateCallContent";
import { CallInProgressContent } from "./CallInProgressContent";
import BillServices from "@/services/billServices";
import { showError } from "@/utils/toast";

export const RefundStage = {
  NONE: "none",
  REQUEST_MODAL: "request_modal",
  LOADING: "loading",
  CALLING: "calling",
  CALL_SUCCESS: "call_success",
} as const;

export type RefundStageType = (typeof RefundStage)[keyof typeof RefundStage];

type Props = {
  visible: boolean;
  item: RefundProcessItem;
  onClose: () => void;
  selectedPrice?: ProductPrice;
  autoStart?: boolean;
};

const PhoneRefundFlowModal: React.FC<Props> = ({
  visible,
  item,
  onClose,
  selectedPrice,
  autoStart = false,
}) => {
  const [refundStage, setRefundStage] = useState<RefundStageType>(
    autoStart ? RefundStage.LOADING : RefundStage.REQUEST_MODAL
  );
  const [sessionId, setSessionId] = useState<string | null>(null);

  const handleCountdownComplete = () => {
    setRefundStage(RefundStage.CALL_SUCCESS);
  };

  const handleRequestRefund = useCallback((sessionId: string) => {
    setSessionId(sessionId);
    setRefundStage(RefundStage.CALLING);
  }, []);

  const handleClose = useCallback(() => {
    setRefundStage(RefundStage.REQUEST_MODAL);
    setSessionId(null);
    onClose();
  }, [onClose]);

  const handleBackdropPress = () => {
    handleClose();
  };

  const startBillRefund = useCallback(
    async (bill: BillWithRefundProcess) => {
      try {
        const data = await BillServices.startRefund(
          bill.accountNumber,
          bill.billingDate
        );
        if (data) {
          handleRequestRefund(data.session.sessionId);
        }
      } catch (error) {
        console.error("Auto refund failed:", error);
        showError("Failed to initiate refund process");
      }
    },
    [handleRequestRefund]
  );

  useEffect(() => {
    if (
      autoStart &&
      visible &&
      refundStage === RefundStage.LOADING &&
      isBill(item)
    ) {
      startBillRefund(item);
    }
  }, [autoStart, visible, refundStage, item, startBillRefund]);

  const renderContent = () => {
    switch (refundStage) {
      case RefundStage.REQUEST_MODAL:
        return (
          <ConfirmCallContent
            item={item}
            onRequestRefund={handleRequestRefund}
            onClose={handleClose}
            selectedPrice={selectedPrice}
          />
        );

      case RefundStage.LOADING:
        return (
          <View style={styles.loadingContainer}>
            <ActivityIndicator size="large" color="#0F9129" />
          </View>
        );

      case RefundStage.CALLING:
        return (
          <InitiateCallContent
            item={item}
            onCountdownComplete={handleCountdownComplete}
            onClose={handleClose}
          />
        );

      case RefundStage.CALL_SUCCESS:
        if (!sessionId) {
          throw new Error("Session ID is required for call success stage");
        }
        return (
          <CallInProgressContent
            item={item}
            sessionId={sessionId}
            onClose={handleClose}
          />
        );

      default:
        throw new Error("Invalid refund stage");
    }
  };

  return (
    <Modal
      visible={visible}
      transparent
      animationType="fade"
      onRequestClose={onClose}
    >
      <GestureHandlerRootView style={styles.gestureRoot}>
        <TouchableWithoutFeedback onPress={handleBackdropPress}>
          <View style={styles.backdrop}>
            <TouchableWithoutFeedback onPress={() => {}}>
              <View style={styles.modalContainer}>
                <TouchableOpacity
                  style={styles.closeButton}
                  onPress={handleClose}
                >
                  <AntDesign name="close" size={20} color="#6B7280" />
                </TouchableOpacity>

                {renderContent()}
              </View>
            </TouchableWithoutFeedback>
          </View>
        </TouchableWithoutFeedback>
      </GestureHandlerRootView>
    </Modal>
  );
};

const styles = StyleSheet.create({
  gestureRoot: {
    flex: 1,
  },
  backdrop: {
    flex: 1,
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    justifyContent: "center",
    alignItems: "center",
    paddingHorizontal: 20,
  },
  modalContainer: {
    backgroundColor: "#FFFFFF",
    borderRadius: 16,
    width: 355,
    position: "relative",
    padding: 20,
  },
  closeButton: {
    position: "absolute",
    top: 16,
    right: 16,
    width: 24,
    height: 24,
    justifyContent: "center",
    alignItems: "center",
    zIndex: 1,
  },
  loadingContainer: {
    padding: 60,
    alignItems: "center",
    justifyContent: "center",
  },
});

export { PhoneRefundFlowModal };
